import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UploadService } from 'app/shared/services/data/upload-service';
import { LoginService } from 'app/shared/services/login/login.service';
import swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { LoginConfirmComponent } from '../../login-confirm/login-confirm.component';
import { LoginConfirm } from 'app/model/user';
import { HttpStatusCode } from '@angular/common/http';
import { FILE_TYPE } from 'app/model/file-type';

@Component({
  selector: 'app-view-document',
  templateUrl: './view-document.component.html',
  styleUrls: ['./view-document.component.scss']
})
export class ViewDocumentComponent implements OnInit, OnChanges {
  private readonly FILE_TYPE_OTHER: string = FILE_TYPE.GENERALS

  private activeModals: NgbModalRef[]

  private rowId: number
  private rowName: string
  private rowType: 'nomina' | 'contrato' | 'otros'
  private rowExt: string

  public safeUrl: SafeResourceUrl

  @Input() public row: any

  @ViewChild('modalContent') public modalContent: any

  /**
   * Constructor
   * 
   * @param {UploadService} uploadService
   * @param {LoginService} loginService
   * @param {NgbModal} modalService 
   * @param {DomSanitizer} sanitizer
   * 
   */
  constructor(
    private uploadService: UploadService,
    private loginService: LoginService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.activeModals = []
  }

  ngOnChanges(changes: SimpleChanges): void {
    const row = changes['row']

    if (row && row.firstChange && row.currentValue) this.setRowValues()
  }

  private setRowValues(): void {
    const { id, name, type, ext } = this.row

    this.rowId = id
    this.rowName = name
    this.rowType = type
    this.rowExt = ext
  }

  public onPreviewFile(): void {
    const isAdmin = this.loginService.isAdmin()

    isAdmin || this.rowType === this.FILE_TYPE_OTHER ? this.previewFile() : this.openLoginConfirmModal()
  }

  public previewFile(loginConfirm?: LoginConfirm): void {
    this.uploadService.getUploadedFileById(this.rowId, loginConfirm).subscribe(
      (blob: Blob) => {
        const file = new File([blob], this.rowName, { type: `application/${this.rowExt}` })

        const url = URL.createObjectURL(file)

        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url)

        this.modalService.dismissAll()

        this.openPreviewModal(this.modalContent)
      },
      (error) => {
        const htmlText = error.status === HttpStatusCode.Forbidden
          ? `Ha ocurrido un error al intentar previsualizar el documento <br><br> Datos inválidos.`
          : `Ha ocurrido un error al intentar previsualizar el documento <br><br> Póngase en contacto con un administrador.`

        const alertOptions: SweetAlertOptions = {
          title: '¡Error!',
          html: htmlText,
          icon: 'error',
          customClass: {
            confirmButton: 'btn red-bg text-white ml-1'
          },
        }

        this.fireSwalAlert(alertOptions)

        this.modalService.dismissAll()
      }
    )
  }

  private openLoginConfirmModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(LoginConfirmComponent)

    modalRef.componentInstance.onLoginConfirm.subscribe((data) => this.previewFile(data))

    this.activeModals.push(modalRef)

    modalRef.result
      .then(() => this.deleteModalRef(modalRef))
      .catch(() => this.deleteModalRef(modalRef))
  }

  public openPreviewModal(modalContent: any): void {
    const modalRef: NgbModalRef = this.modalService.open(modalContent, {
      windowClass: 'view-document'
    })

    this.activeModals.push(modalRef)

    modalRef.result
      .then(() => this.deleteModalRef(modalRef))
      .catch(() => this.deleteModalRef(modalRef))
  }

  private deleteModalRef(modalRef: NgbModalRef): void {
    this.activeModals = this.activeModals.filter(item => item !== modalRef)
  }

  private fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return swal.fire(options)
  }
}
